import { useNavigate } from 'react-router-dom';
import type { NextAction } from '../../utils/dashboard';
import { formatMinutes } from '../../utils/dashboard';
import { dashboardCopy } from '../../copy/dashboard';
import WhyAffordance from '../ai/WhyAffordance';
import { Button } from '@/components/ui/button';

// ---------------------------------------------------------------------------
// NextActionCard — the hero of the new dashboard. Exactly one primary action,
// verb-first, time-bound when possible. Absence of data is handled with a
// single skeleton row, not a multi-section loading state.
// ---------------------------------------------------------------------------

interface NextActionCardProps {
  action: NextAction | null;
  loading?: boolean;
  /** AI reasoning behind why this is the next action. Optional — hides the
   *  affordance entirely when absent. */
  rationale?: string;
  /** Secondary line for the rationale popover (e.g. strategy link). */
  rationaleStrategyLink?: string;
  /** Callback invoked when the "Plan tomorrow" verb fires the primary
   *  button. Without this the button used to navigate to action.href ('/'),
   *  which dumped the user back on the dashboard they were already on. */
  onPlanTomorrow?: () => void;
}

export function NextActionCard({
  action,
  loading = false,
  rationale,
  rationaleStrategyLink,
  onPlanTomorrow,
}: NextActionCardProps) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="rounded-3xl border border-border bg-card/60 p-8 backdrop-blur">
        <div className="h-4 w-20 animate-pulse rounded bg-muted" />
        <div className="mt-2 h-10 w-3/5 animate-pulse rounded bg-muted" />
        <div className="mt-2 h-6 w-48 animate-pulse rounded bg-muted" />
        <div className="mt-6 h-11 w-44 animate-pulse rounded-md bg-muted" />
      </div>
    );
  }

  if (!action) return null;

  const handlePrimary = () => {
    // Plan-tomorrow short-circuits navigation: trigger the API call directly
    // so a single click moves the user from "today is done" to "tomorrow is
    // ready" instead of bouncing them through another route.
    if (action.verb === 'Plan tomorrow' && onPlanTomorrow) {
      onPlanTomorrow();
      return;
    }
    if (action.href) navigate(action.href);
  };

  const timePrefix = action.startTime
    ? `${action.startTime}${action.durationMinutes ? ` · ${formatMinutes(action.durationMinutes)}` : ''}`
    : action.durationMinutes
      ? formatMinutes(action.durationMinutes)
      : undefined;

  return (
    <div className="relative overflow-hidden rounded-3xl border border-border bg-card/60 p-6 backdrop-blur md:p-8">
      {/* Subtle accent bar on the left anchors the card without making it
          shout. Colour comes from the theme primary so dark/light both work. */}
      <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />

      <div className="flex items-center gap-1">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {dashboardCopy.nextActionHeader}
        </p>
        <WhyAffordance
          rationale={rationale}
          secondary={rationaleStrategyLink}
          size="compact"
          label="Why this next"
        />
      </div>

      <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-end md:gap-8">
        <div className="min-w-0 flex-1">
          <h2 className="line-clamp-2 text-2xl font-bold tracking-tight md:text-3xl">
            {action.verb} {action.label}
          </h2>
          {action.context && (
            <p className="mt-1 text-sm text-muted-foreground">{action.context}</p>
          )}
        </div>

        {timePrefix && (
          <p className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
            {timePrefix}
          </p>
        )}
      </div>

      {action.href && (
        <Button size="lg" onClick={handlePrimary} className="mt-6 font-semibold">
          {primaryLabelFor(action)}
        </Button>
      )}
    </div>
  );
}

// Map the derived action verb to a button label. Keeping the label close to
// the heading reduces cognitive load — the button repeats the commitment the
// user already read, in a shorter form.
function primaryLabelFor(action: NextAction): string {
  switch (action.verb) {
    case 'Start':
      return dashboardCopy.startWorkItem;
    case 'Draft':
    case 'Review':
    case 'Decide on':
    case 'Follow up on':
    case 'Read':
    case 'Read briefing':
      return 'Open work item';
    case 'Prepare for':
      return dashboardCopy.startPreparingMeeting;
    case 'Plan tomorrow':
      return 'Plan tomorrow';
    default:
      return 'Open';
  }
}

export default NextActionCard;
